/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useEffect, useState } from "react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { deleteCalibration } from "src/graphql/mutations";
import toast from "react-hot-toast";
import {
  UserPlusIcon,
  PencilSquareIcon,
  DocumentIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

import { queryCalibrations } from "src/services/AmplifyQuery";

import AddCalibration from "src/components/modals/AddCalibration.react";
import RemoveModal from "src/components/modals/RemoveModal.react";

/**
 * The Calibration component
 *
 * @author Thais Watanabe
 */
const Calibration = () => {
  const [calibrations, setCalibrations] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [selectedCalibration, setSelectedCalibration] = useState(null);

  /* useEffect */
  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);
  }, []);

  /* useEffect */
  useEffect(() => {
    fetchCalibrations();
  }, []);

  /**
   * fetchCalibrations
   */
  async function fetchCalibrations() {
    queryCalibrations(null, setCalibrations);
  }

  /**
   * openRemoveDialog
   * @param {*} photo
   */
  async function openRemoveDialog(calibration) {
    setSelectedCalibration(calibration);
    setIsRemoveModalOpen(true);
  }

  /**
   * closeRemoveDialog
   */
  async function closeRemoveDialog() {
    setSelectedCalibration(null);
    setIsRemoveModalOpen(false);
  }

  /**
   * removeCalibration
   */
  async function removeCalibration() {
    try {
      await API.graphql(
        graphqlOperation(deleteCalibration, {
          input: {
            id: selectedCalibration.id,
            _version: selectedCalibration._version,
          },
        })
      );

      const updatedCalibrations = calibrations.filter(
        (calibration) => calibration.id !== selectedCalibration.id
      );
      setCalibrations(updatedCalibrations);
      closeRemoveDialog();
      toast.success("保存しました");
    } catch (err) {
      console.log(err);
      toast.error("エラーが発生しました");
    }
  }

  /**
   * showEdit
   * @param {*} calibration
   */
  function showEdit(calibration) {
    setSelectedCalibration(calibration);
    setIsOpen(!isOpen);
  }

  /**
   * downloadFile
   * @param {*} s3Path
   */
  async function downloadFile(s3Path) {
    const url = await Storage.get(s3Path);
    window.open(url, "_blank", "noreferrer");
  }

  return (
    <>
      <section aria-labelledby="billing_history_heading">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl lg:px-8">
          <div className="flex items-center space-x-5">
            <div>
              <h2
                id="billing_history_heading"
                className="text-lg leading-6 font-medium text-gray-900"
              >
                機材校正一覧
              </h2>
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 md:mt-0 md:flex-row md:space-x-3">
            <button
              type="button"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
              onClick={() => setIsOpen(!isOpen)}
            >
              {/* <!-- Heroicon name: user-add  --> */}
              <UserPlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
              新規機材校正登録
            </button>
          </div>
        </div>
        <div className="mt-6 flex flex-col overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  管理番号
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  機材名称
                </th>

                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  メーカー
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  型式番号
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  シリアル番号
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  校正有効期限
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  期限の記載
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  校正証明書
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                ></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {calibrations.length < 1 && (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                  >
                    <div className="loader"></div>
                  </td>
                </tr>
              )}
              {calibrations
                .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
                .map((calibration, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {calibration.managementNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {calibration.tool.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {calibration.maker.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {calibration.modelNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {calibration.serialNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {calibration.expirationDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {calibration.expirationObs}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {calibration.certificateS3Path && (
                        <button
                          onClick={() =>
                            downloadFile(calibration.certificateS3Path)
                          }
                          className="rounded-full flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
                        >
                          {/* <!-- Heroicon name: trash --> */}
                          <DocumentIcon className="h-5 w-5" />
                        </button>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => showEdit(calibration)}
                        className="rounded-full flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
                      >
                        {/* <!-- Heroicon name: pencil-alt --> */}
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openRemoveDialog(calibration)}
                        className="rounded-full flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
                      >
                        {/* <!-- Heroicon name: trash --> */}
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      <AddCalibration
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        calibrations={calibrations}
        setCalibrations={setCalibrations}
        selectedCalibration={selectedCalibration}
        setSelectedCalibration={setSelectedCalibration}
      />

      <RemoveModal
        open={isRemoveModalOpen}
        setOpen={setIsRemoveModalOpen}
        remove={removeCalibration}
      />
    </>
  );
};

export default Calibration;
